/*
 * ===========================================================================
 * [StrongGate Limited] [http://www.stronggate.com]
 * Copyright (c) 2022. All rights Reserved.
 * Unauthorized access, use, dissemination, distribution, reproduction of this
 * file, or a portion of, in any form is prohibited and unlawful. If you receive
 * this file, and you are not an authorized company employee, or you believe you
 * have received this file in error, please notify the sender (or originator)
 * and promptly delete this file.
 *
 * Project:
 * File: TeamService.ts
 * Created by: ChanYing
 * Create Date: 2022-12-08 09:47
 * Version Number: 1.0
 * Version Description:
 *
 * =============================================================================
 */

import ApiUrlBuilder from "@/commons/https/ApiUrlBuilder";
import AppConfig from "@/commons/AppConfig";
import HttpExecutor from "@/commons/https/HttpExecutor";
import HttpRequestGet from "@/commons/https/HttpRequestGet";
import Result from "@/commons/Result";
import Log from "@/commons/logs/Log";
import HttpTokenProvider from "@/commons/https/HttpTokenProvider";
import TimezoneContinent from "@/components/ruleseteditor/TimezoneContinent";
import {TeamInfoVO} from "@/mvc/models/team/TeamInfoVO";
import ContinentInfoVO from "@/mvc/models/team/ContinentInfoVO";
import {HttpStatus} from "@/commons/https/HttpStatus";

export default class TeamService {

    // ------------------------------------------------------------------------------------
    // singleton
    // ------------------------------------------------------------------------------------
    private static _instance: TeamService;

    public static get Instance() {
        return this._instance || (this._instance = new this());
    }

    public constructor() {
    }

    /**
     * Retrieves a list of continents.
     *
     * @return {Promise<Result<ContinentInfoVO[]>>} A promise that resolves to a Result object containing an array of ContinentInfoVO data, or an error status if the request fails.
     */
    public getContinentList(): Promise<Result<ContinentInfoVO[]>> {
        let req = new HttpRequestGet(ApiUrlBuilder.InstanceTeam,
            `/cld-service-teamsite/api/v1/teams/data/continent`,
            {
                timeout: AppConfig.Instance.apiDefaultTimeout,
            });

        return new Promise<Result<ContinentInfoVO[]>>((resolve) => {
            HttpExecutor.Instance.execute(req, HttpTokenProvider.TeamService).then((res) => {
                resolve(Result.of<ContinentInfoVO[]>(res.status, res.data));
                return;
            }).catch((err) => {
                Log.error("error on get continent, cause: " + err);
                const status = err?.response?.status ?? HttpStatus.StatusBadGateway;
                resolve(Result.of<ContinentInfoVO[]>(status));
            });
        });
    }

    /**
     * Retrieves a list of holidays using an HTTP GET request to the specified API endpoint.
     *
     * @return {Promise<Result<void>>} A promise resolving to a Result object containing the status and response data, or an error status in case of failure.
     */
    public getHolidaysList(): Promise<Result<void>> {
        let req = new HttpRequestGet(ApiUrlBuilder.InstanceTeam,
            `/cld-service-teamsite/api/v1/teams/data/holidays`,
            {
                timeout: AppConfig.Instance.apiDefaultTimeout,
            });
        return new Promise<Result<void>>((resolve) => {
            HttpExecutor.Instance.execute(req, HttpTokenProvider.TeamService).then((res) => {
                resolve(Result.of<void>(res.status, res.data));
                return;
            }).catch((err) => {
                Log.error("error on get holidays, cause: " + err);
                const status = err?.response?.status ?? HttpStatus.StatusBadGateway;
                resolve(Result.of<void>(status));
            });
        });
    }

    /**
     * Retrieves the name of a continent based on the provided continent ID.
     *
     * @param continentId The unique identifier of the continent.
     * @return A promise that resolves to a Result object containing the continent name as a string, or an error status if the operation fails.
     */
    public getContinentName(continentId: number): Promise<Result<string>> {
        let req = new HttpRequestGet(ApiUrlBuilder.InstanceTeam,
            `/cld-service-teamsite/api/v1/teams/data/continent/${ApiUrlBuilder.seg(continentId)}`,
            {
                timeout: AppConfig.Instance.apiDefaultTimeout,
            });
        return new Promise<Result<string>>((resolve) => {
            HttpExecutor.Instance.execute(req, HttpTokenProvider.TeamService).then((res) => {
                resolve(Result.of<string>(res.status, res.data));
                return;
            }).catch((err) => {
                Log.error("error on get continent name, cause: " + err);
                const status = err?.response?.status ?? HttpStatus.StatusBadGateway;
                resolve(Result.of<string>(status));
            });
        });
    }

    /**
     * Retrieves the name of a region based on the provided region ID.
     *
     * @param regionId The unique identifier of the region whose name is to be fetched.
     * @return A Promise that resolves to a Result object containing the region name as a string.
     *         If an error occurs, it resolves to a Result indicating the error status.
     */
    public getRegionName(regionId: number): Promise<Result<string>> {
        let req = new HttpRequestGet(ApiUrlBuilder.InstanceTeam,
            `/cld-service-teamsite/api/v1/teams/data/region/${ApiUrlBuilder.seg(regionId)}`,
            {
                timeout: AppConfig.Instance.apiDefaultTimeout,
            });
        return new Promise<Result<string>>((resolve) => {
            HttpExecutor.Instance.execute(req, HttpTokenProvider.TeamService).then((res) => {
                resolve(Result.of<string>(res.status, res.data));
                return;
            }).catch((err) => {
                Log.error("error on get region name, cause: " + err);
                const status = err?.response?.status ?? HttpStatus.StatusBadGateway;
                resolve(Result.of<string>(status));
            });
        });
    }

    /**
     * Fetches the list of timezones categorized by continent from the team service.
     * Makes an HTTP GET request to the specified endpoint to retrieve the data.
     *
     * @return {Promise<Result<TimezoneContinent[]>>} A promise that resolves with a `Result` object containing an array of `TimezoneContinent`, or an error status if the request fails.
     */
    public getTimezone(): Promise<Result<TimezoneContinent[]>> {
        let req = new HttpRequestGet(ApiUrlBuilder.InstanceTeam,
            `/cld-service-teamsite/api/v1/teams/data/timezones`,
            {
                timeout: AppConfig.Instance.apiDefaultTimeout,
            });
        return new Promise<Result<TimezoneContinent[]>>((resolve) => {
            HttpExecutor.Instance.execute(req, HttpTokenProvider.TeamService).then((res) => {
                resolve(Result.of<TimezoneContinent[]>(res.status, res.data));
                return;
            }).catch((err) => {
                Log.error("error on get timezone, cause: " + err);
                const status = err?.response?.status ?? HttpStatus.StatusBadGateway;
                resolve(Result.of<TimezoneContinent[]>(status));
            });
        });
    }

    /**
     * Retrieves the team information by making an HTTP GET request to the specified endpoint.
     *
     * This method communicates with the team service using the configured HTTP client,
     * handles success and error scenarios, and resolves a `Promise` with a `Result` object
     * containing the response data or an error status code.
     *
     * @return {Promise<Result<TeamInfoVO>>} A promise that resolves with the `Result` object
     * containing the team information on success, or an appropriate error status code on failure.
     */
    public retrieve(): Promise<Result<TeamInfoVO>> {
        let req = new HttpRequestGet(ApiUrlBuilder.InstanceTeam,
            `/cld-service-teamsite/api/v1/teams`,
            {
                timeout: AppConfig.Instance.apiDefaultTimeout,
            });
        return new Promise<Result<TeamInfoVO>>((resolve) => {
            HttpExecutor.Instance.execute(req, HttpTokenProvider.TeamService).then((res) => {
                resolve(Result.of<TeamInfoVO>(res.status, res.data));
                return;
            }).catch((err) => {
                Log.error("error on retrieve team info, cause: " + err);
                const status = err?.response?.status ?? HttpStatus.StatusBadGateway;
                resolve(Result.of<TeamInfoVO>(status));
            });
        });
    }

    /**
     * Retrieves team information based on the provided team domain.
     *
     * @param {string} teamDomain - The domain of the team for which information is to be retrieved.
     * @return {Promise<Result<TeamInfoVO>>} A promise that resolves with the result containing the team information or an error status.
     */
    public retrieveTeamInfoByDomain(teamDomain: string): Promise<Result<TeamInfoVO>> {
        let req = new HttpRequestGet(ApiUrlBuilder.InstanceTeam,
            `/cld-service-teamsite/api/v1/teams/${ApiUrlBuilder.seg(teamDomain)}`,
            {
                timeout: AppConfig.Instance.apiDefaultTimeout,
            });
        return new Promise<Result<TeamInfoVO>>((resolve) => {
            HttpExecutor.Instance.execute(req, HttpTokenProvider.TeamService).then((res) => {
                resolve(Result.of<TeamInfoVO>(res.status, res.data));
                return;
            }).catch((err) => {
                Log.error("error on retrieve team info, cause: " + err);
                const status = err?.response?.status ?? HttpStatus.StatusBadGateway;
                resolve(Result.of<TeamInfoVO>(status));
            });
        });
    }

    /**
     * Retrieves detailed information for the given team IDs.
     *
     * @param {string[]} ids - Array of team IDs for which to retrieve information.
     * @return {Promise<Result<TeamInfoVO[]>>} A promise that resolves to a Result object containing an array of TeamInfoVO, or an error status if the operation fails.
     */
    public retrieveTeamInfoByIds(ids: string[]): Promise<Result<TeamInfoVO[]>> {
        let params = new URLSearchParams();
        params.append("teamIds", ids.join(","));
        let req = new HttpRequestGet(ApiUrlBuilder.InstanceTeam,
            `/cld-service-teamsite/api/v1/teams/team-ids`,
            {
                timeout: AppConfig.Instance.apiDefaultTimeout,
            }, params);
        return new Promise<Result<TeamInfoVO[]>>((resolve) => {
            HttpExecutor.Instance.execute(req, HttpTokenProvider.TeamService).then((res) => {
                resolve(Result.of<TeamInfoVO[]>(res.status, res.data));
                return;
            }).catch((err) => {
                Log.error("error on retrieve team info, cause: " + err);
                const status = err?.response?.status ?? HttpStatus.StatusBadGateway;
                resolve(Result.of<TeamInfoVO[]>(status));
            });
        });
    }

    //
    public temp(): Promise<Result<any>> {
        let req = new HttpRequestGet(ApiUrlBuilder.InstanceTeam,
            `/cld-service-teamsite/api/v1/teams/temp`,
            {
                timeout: AppConfig.Instance.apiDefaultTimeout,
            });
        return new Promise<Result<any>>((resolve) => {
            HttpExecutor.Instance.execute(req, HttpTokenProvider.TeamService).then((res) => {
                resolve(Result.of<any>(res.status, res.data));
                return;
            }).catch((err) => {
                Log.error("error on retrieve team info, cause: " + err);
                const status = err?.response?.status ?? HttpStatus.StatusBadGateway;
                resolve(Result.of<any>(status));
            });
        });
    }
}
